import { useTranslation } from 'react-i18next'
import { useField, useFormikContext } from 'formik'

import { EMPTY_STRING } from '../../../../constants/common'
import {
  AI_PROMPT_MAX_LENGTH,
  AI_TEXT_GENERATION_FORM_FIELD_NAME_LENGTH,
  AI_TEXT_GENERATION_FORM_FIELD_NAME_PROMPT,
  AI_TEXT_GENERATION_FORM_FIELD_NAME_TONE,
} from '../../AITextGeneration.constants'
import {
  getAITextGenerationTranscriptBudget,
  isAITextGenerationSettingsChanged,
} from '../../AITextGeneration.utils'
import { useAIVoiceInputState } from '../useAIVoiceInputState'

export function useAITextGenerationFormFieldsState({
  disabled,
  errorMessage,
  hasFieldContent,
  hasGeneratedOnce,
  isGenerating,
  lastSubmittedSettings,
}) {
  const { t } = useTranslation()

  const { setFieldValue, submitForm, values } = useFormikContext()

  const [{ value: promptValue }] = useField(
    AI_TEXT_GENERATION_FORM_FIELD_NAME_PROMPT
  )

  function getCurrentPromptValue() {
    return values[AI_TEXT_GENERATION_FORM_FIELD_NAME_PROMPT] ?? EMPTY_STRING
  }

  function handleVoiceDisplayValueChange(nextValue) {
    setFieldValue(AI_TEXT_GENERATION_FORM_FIELD_NAME_PROMPT, nextValue)
  }

  const voice = useAIVoiceInputState({
    getCurrentPromptValue,
    onDisplayValueChange: handleVoiceDisplayValueChange,
  })

  const hasPrompt = Boolean(promptValue?.trim())

  const isPromptLimitReached =
    getAITextGenerationTranscriptBudget(promptValue, AI_PROMPT_MAX_LENGTH) <= 0

  const isSettingsChanged = isAITextGenerationSettingsChanged({
    currentLength:
      values[AI_TEXT_GENERATION_FORM_FIELD_NAME_LENGTH]?.value ?? null,
    currentTone: values[AI_TEXT_GENERATION_FORM_FIELD_NAME_TONE]?.value ?? null,
    hasPrompt,
    lastSubmittedSettings,
    promptValue,
  })

  const hasPreparedPrompt = hasPrompt || hasFieldContent || hasGeneratedOnce

  const isInteractionDisabled = isGenerating || voice.isActive || disabled

  const isGenerateDisabled =
    isInteractionDisabled ||
    !hasPreparedPrompt ||
    (hasPreparedPrompt && hasGeneratedOnce && !isSettingsChanged)

  const generateButtonText = hasGeneratedOnce
    ? t('AITextGeneration.RegenerateButtonKey')
    : t('AITextGeneration.GenerateButtonKey')

  const combinedErrorMessage = errorMessage || voice.errorMessage

  return {
    combinedErrorMessage,
    generateButtonText,
    isGenerateDisabled,
    isInteractionDisabled,
    isPromptLimitReached,
    submitForm,
    voice,
  }
}
